// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { type ComparisonAsset } from '@shared/data-access/interfaces/common/comparison-asset';
import { getAssetBrandLogoUrl } from '@shared/data-access/utils';
import type {
  SavedPortfolio,
  Screener,
} from '@shared/data-access/interfaces/redux/my-research-state';
import { type AppState } from '@advisor-app/store/store';
import { type BenchmarkOption } from '@shared/advisory/aggregation/components/benchmark-selector';
import { isUndefined } from '@shared/utils';

export const savedPortfoliosEntityAdapter = createEntityAdapter<
  SavedPortfolio,
  number
>({
  selectId: ({ portfolio_id }) => portfolio_id,
  sortComparer: /* istanbul ignore next */ (current, next) =>
    Date.parse(next.creation_date) - Date.parse(current.creation_date),
});

export const screenersEntityAdapter = createEntityAdapter<Screener, number>({
  selectId: ({ universe_id }) => universe_id,
  sortComparer: /* istanbul ignore next */ (current, next) =>
    Date.parse(next.creation_date) - Date.parse(current.creation_date),
});

const savedPortfoliosSelectors = savedPortfoliosEntityAdapter.getSelectors();

const screenersSelectors = screenersEntityAdapter.getSelectors();

export const selectMyResearchState = ({ myResearch }: AppState) => myResearch;

export const selectSavedPortfolios = createSelector(
  selectMyResearchState,
  ({ savedPortfolios }) => savedPortfolios,
);

export const selectIdeas = createSelector(
  selectSavedPortfolios,
  savedPortfoliosSelectors.selectAll,
);

export const selectIdeasTableData = createSelector(
  selectSavedPortfolios,
  state => state.ideasTableData,
);

export const selectSavedPortfoliosBenchmarks = createSelector(
  selectIdeas,
  portfolios => {
    return portfolios
      .filter(portfolio => portfolio.holdings.length > 0)
      .map(portfolio => {
        const positions = portfolio.holdings
          .slice()
          .sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));

        const position = positions[0]!;

        return {
          value: position.ticker,
          label: portfolio.portfolio,
          ticker: position.ticker,
        } as BenchmarkOption;
      });
  },
);

export const selectScreeners = createSelector(
  selectMyResearchState,
  ({ screeners }) => screeners,
);

export const selectScreenersLoading = createSelector(
  selectScreeners,
  ({ loading }) => loading,
);

export const selectScreenersPositions = createSelector(
  selectScreeners,
  screenersSelectors.selectAll,
);

export const selectComparisonState = createSelector(
  selectMyResearchState,
  ({ comparison }) => comparison,
);

export const selectComparisonBenchmark = createSelector(
  selectComparisonState,
  ({ benchmark }) => benchmark,
);

export const selectComparisonHist = createSelector(
  selectComparisonState,
  ({ w_hist }) => w_hist,
);

export const selectComparisonTickers = createSelector(
  selectComparisonState,
  ({ tickers }) => tickers,
);

export const selectComparisonAssets = createSelector(
  selectComparisonState,
  ({ assetsComparison }) => assetsComparison,
);

export const selectComparisonTable = createSelector(
  selectComparisonState,
  /* istanbul ignore next */ ({ assetsInfo, assetsComparison }) =>
    assetsInfo
      .map(({ ticker, name, expenses_ratio: ter, aum, ...props }) => {
        const comparison = assetsComparison.asset_analysis.find(
          value => value.ticker === ticker,
        )!;

        const backtest = assetsComparison.asset_backtest.find(
          value => value.ticker === ticker,
        )!;

        if (isUndefined(comparison)) return undefined;

        const assetType =
          'url_logo' in props
            ? 'stock'
            : 'etf_company' in props
            ? 'etf'
            : 'mutual_fund';

        return {
          ticker,
          name,
          ter,
          aum,
          currency: 'currency' in props ? props.currency : null,
          url_logo: getAssetBrandLogoUrl(props) ?? 'missing',
          returns_1M: comparison.returns.returns_1M,
          returns_3M: comparison.returns.returns_3M,
          returns_6M: comparison.returns.returns_6M,
          returns_YTD: comparison.returns.returns_YTD,
          vol: comparison.risk.volatility,
          sharpe: comparison.risk.sharpe,
          beta: comparison.risk.beta,
          alpha: comparison.risk.alpha,
          max_drawdown: Math.min(...backtest.ts_drawdowns.portfolio),
          assetType,
        } as ComparisonAsset;
      })
      .filter((value): value is ComparisonAsset => !isUndefined(value)),
);

export const selectLimitedComparisonState = createSelector(
  selectComparisonState,
  ({ tickers, assetsComparison, assetsInfo, benchmark, ...props }) => {
    const limitedTickers = tickers.slice(0, 5);

    const selectedTickers = [...limitedTickers, benchmark];

    const filterByTickers = (array: any[], key: string) =>
      array.filter(item => selectedTickers.includes(item[key]));

    return {
      tickers: selectedTickers,
      assetsComparison: {
        ...assetsComparison,
        asset_analysis: filterByTickers(assetsComparison.asset_analysis, 'ticker'),
        asset_backtest: filterByTickers(assetsComparison.asset_backtest, 'ticker'),
        risk_returns: filterByTickers(assetsComparison.risk_returns, 'ticker'),
        corr_matrix: {
          ticker: selectedTickers,
          correlation_cols: Object.fromEntries(
            Object.entries(assetsComparison.corr_matrix.correlation_cols).filter(
              ([key]) => selectedTickers.includes(key),
            ),
          ),
        },
      },
      assetsInfo: filterByTickers(assetsInfo, 'ticker'),
      ...props,
    };
  },
);

export const selectComparisonCorrMatrix = createSelector(
  selectComparisonState,
  ({ assetsComparison }) => assetsComparison.corr_matrix,
);

export const selectComparisonRisk = createSelector(
  selectComparisonState,
  ({ assetsComparison }) => assetsComparison.risk_returns,
);

export const selectComparisonAnalysis = createSelector(
  selectComparisonState,
  ({ assetsComparison }) => assetsComparison.asset_analysis,
);

export const selectComparisonBacktest = createSelector(
  selectComparisonState,
  ({ assetsComparison }) => assetsComparison.asset_backtest,
);

export const selectComparisonHistoricalData = createSelector(
  selectComparisonState,
  ({ assetsComparison }) => assetsComparison.historical_data,
);

export const selectComparisonAssetsInfo = createSelector(
  selectComparisonState,
  ({ assetsInfo }) => assetsInfo,
);

export const selectMutualFundsFilters = createSelector(
  selectScreeners,
  ({ mutualFundsFilters }) => mutualFundsFilters,
);

export const selectEtfsFilters = createSelector(
  selectScreeners,
  ({ etfsFilters }) => etfsFilters,
);
